import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { Brand, TopControls } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const { t, setUser, loadProperties } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!token) setErr(t("invalidResetLink")); }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) { setErr(t("passwordsMismatch")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      if (data.token) localStorage.setItem("rai_token", data.token);
      toast.success(t("resetSuccess"));
      if (data.user) { setUser(data.user); await loadProperties(); }
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1718030463382-896949a8d53a" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40 mix-blend-multiply" />
      </div>
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 relative">
        <div className="absolute top-6 right-6"><TopControls /></div>
        <div className="mb-10"><Brand onClick={() => navigate("/")} /></div>
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-primary" />
        </div>
        <h1 className="text-3xl font-bold mb-2">{t("resetTitle")}</h1>
        <p className="text-muted-foreground mb-8">{t("passwordMinNote")}</p>
        <form onSubmit={submit} className="space-y-5 max-w-sm">
          <div><Label>{t("newPassword")}</Label>
            <Input data-testid="reset-password" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" disabled={!token} /></div>
          <div><Label>{t("confirmPassword")}</Label>
            <Input data-testid="reset-password-confirm" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" disabled={!token} /></div>
          {err && <p data-testid="reset-error" className="text-sm text-destructive">{err}</p>}
          <Button data-testid="reset-submit" type="submit" className="w-full" disabled={busy || !token}>
            {busy ? t("loading") : t("updatePassword")}
          </Button>
        </form>
        <Link data-testid="reset-back-login" to="/login" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline mt-6">
          <ArrowLeft className="w-4 h-4" />{t("backToLogin")}
        </Link>
      </div>
    </div>
  );
}
